import { Box, Button, Flex, Text, Textarea } from "@chakra-ui/react";
import { AgentTaskMemoryClickPayload, AgentTaskProgress, useAgentTasks } from "@fencyai/react";
import { Fragment, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SignedDocumentDto, ViewDocumentAsOwnerDto } from "../../autogen/bff-api";
import { useChatHistory, type ChatMessage } from "../fency/useChatHistory";

type FencyMemoryChatProps = {
  document: ViewDocumentAsOwnerDto | SignedDocumentDto;
  fetchCreateMemoryChatClientToken: (
    doc: ViewDocumentAsOwnerDto | SignedDocumentDto,
  ) => Promise<{ clientToken: string }>;
};

export const FencyMemoryChat = ({ document, fetchCreateMemoryChatClientToken }: FencyMemoryChatProps) => {
  const { t } = useTranslation();
  const chatEndRef = useRef<HTMLDivElement>(null);

  const fetchToken = useCallback(
    async (taskType: "StreamingChatCompletion" | "StructuredChatCompletion" | "MemoryChat" | "MemorySearch") => {
      void taskType;
      return fetchCreateMemoryChatClientToken(document);
    },
    [document, fetchCreateMemoryChatClientToken],
  );

  const { createAgentTask, agentTasks } = useAgentTasks({
    fetchCreateAgentTaskClientToken: fetchToken,
  });

  const sendMessages = useCallback(
    async (messages: ChatMessage[]) => {
      const result = await createAgentTask({
        type: "MemoryChat",
        messages,
        model: "google/gemini-2.5-flash",
        language: "no",
        memoryScanLimit: 3,
        chunkLimit: 20,
      });

      if (result.type === "success") {
        const response = result.response;
        if (response.taskType === "MemoryChat" && response.eventType === "Completed") {
          return response.response.textResponse;
        }
      }
      return null;
    },
    [createAgentTask],
  );

  const { userMessages, prompt, setPrompt, isLoading, handleSend } = useChatHistory({
    documentName: document.name,
    sendMessages,
  });

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [userMessages, agentTasks]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSourceClick = useCallback(async (payload: AgentTaskMemoryClickPayload) => {
    console.log("source click", payload);
  }, []);

  return (
    <Flex flex={1} flexDirection="column" minWidth={0} minH={0} overflow="hidden">
      <Box flex={1} minHeight={0} overflowY="auto" py="4" px="2" display="flex" flexDirection="column" gap="3">
        {userMessages.length === 0 && (
          <Flex flex={1} alignItems="center" justifyContent="center" color="gray.500" fontSize="sm" textAlign="center">
            <Text>{`${t("Ask something about this document")}...`}</Text>
          </Flex>
        )}
        {userMessages.map((content, i) => (
          <Fragment key={`msg-${i}`}>
            <Flex justifyContent="flex-end">
              <Box
                maxWidth="75%"
                px="4"
                py="2"
                borderRadius="16px 16px 4px 16px"
                backgroundColor="teal.500"
                color="white"
                fontSize="sm"
                lineHeight="tall"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
              >
                {content}
              </Box>
            </Flex>
            {agentTasks[i] && (
              <Flex justifyContent="flex-start">
                <Box maxWidth="100%" width="100%">
                  <AgentTaskProgress agentTask={agentTasks[i]} mode="verbose" onSourceClick={handleSourceClick} />
                </Box>
              </Flex>
            )}
          </Fragment>
        ))}
        <div ref={chatEndRef} />
      </Box>

      <Flex pt="3" pb="4" borderTopWidth="1px" borderColor="gray.200" gap="2" alignItems="flex-end" flexShrink={0}>
        <Textarea
          placeholder={`${t("Ask something about this document")}…`}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          flex={1}
          resize="none"
        />
        <Button onClick={handleSend} colorScheme="teal" flexShrink={0} height="56px" px="5" isDisabled={isLoading}>
          {t("Send")}
        </Button>
      </Flex>
    </Flex>
  );
};
